import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const KIT_URL = "/correlate-press-kit.zip";
const PROMO_VIDEO_URL = "/correlate-promo-1min.mp4";
const PROMO_VIDEO_LONG_URL = "/correlate-promo-2min.mp4";
const ONE_LINER = "Correlate is a scholar-mystic archive that shows the same truth spoken in different tongues — across religions, esoteric lineages, hidden history, and modern science.";
const LONG_BLURB = "Correlate is a mobile-first archive and social space for people who suspect the great traditions are pointing at the same territory under different names. Browse a growing lexicon of traditions side-by-side, ask the Oracle (a scholar-mystic AI that cites its sources and labels every claim Academic, Esoteric, Speculative, or Redacted), share your own correlations as vertical Comparison or Insight cards, and read a weekly feed shaped by the community. Built for seekers, scholars, and curious skeptics alike.";

const QUICK_FACTS = [
  ["Product",         "Correlate — cross-tradition archive + Oracle AI + social feed"],
  ["Platforms",       "Progressive Web App (iOS, Android via Play Store, Web)"],
  ["Founded",         "2025"],
  ["Category",        "Education · Reference · Spirituality · Comparative religion"],
  ["Pricing",         "Free"],
  ["Website",         "https://correlate.online"],
  ["Contact",         "press@correlate.online"],
];

const ASSETS = [
  { label: "App icon (512×512)",             href: "/correlate-play-store-assets.zip#icon", direct: "https://unified-frameworks.preview.emergentagent.com/correlate-play-store-assets.zip" },
  { label: "Feature graphic (1024×500)",      direct: null },
  { label: "Phone screenshots (5×, 1170×2080 portrait)", direct: null },
  { label: "Tablet screenshots (5×, 1200×1920)", direct: null },
  { label: "Desktop screenshots (5×, 1920×1080)", direct: null },
];

export default function Press() {
  const copyBlurb = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Copy failed. Select and copy manually.");
    }
  };

  return (
    <div data-testid="press-page" className="max-w-3xl mx-auto px-6 sm:px-10 py-16 sm:py-24">
      <div className="label mb-4 text-[hsl(var(--accent))]">Press &amp; Media</div>
      <h1 className="serif text-5xl text-foreground leading-tight">
        Everything you need to write about Correlate.
      </h1>
      <p className="mt-6 serif italic text-lg text-muted-foreground leading-relaxed">
        {ONE_LINER}
      </p>

      <div className="mt-10 flex flex-wrap gap-3">
        <a
          data-testid="press-download-kit"
          href={KIT_URL}
          download
          className="btn-gold inline-flex items-center gap-2"
        >
          ↓ Download the press kit (ZIP)
        </a>
        <button
          data-testid="press-copy-oneliner"
          onClick={() => copyBlurb(ONE_LINER, "One-liner")}
          className="btn-ghost"
        >
          Copy one-liner
        </button>
        <button
          data-testid="press-copy-longblurb"
          onClick={() => copyBlurb(LONG_BLURB, "Long blurb")}
          className="btn-ghost"
        >
          Copy long blurb
        </button>
      </div>

      <Section title="60-second promo video">
        <p className="mb-4">
          Silent, 16:9, animated screen-recording walk-through — Traditions →
          Quantum Physics → Compare with The Law of One → Oracle response →
          Share to Weekly → X · Facebook · Instagram (etc.). A synthetic
          magnifier hovers on each button before the click so the navigation is
          crystal clear. Drop your own voiceover using the timed script inside
          the press kit (<code className="mono text-[13px]">VOICEOVER_SCRIPT_1MIN.md</code>).
        </p>
        <video
          data-testid="press-promo-video"
          controls
          preload="metadata"
          playsInline
          className="w-full aspect-video bg-black"
          src={PROMO_VIDEO_URL}
        />
        <div className="mt-4 flex flex-wrap gap-3">
          <a
            data-testid="press-download-video"
            href={PROMO_VIDEO_URL}
            download
            className="btn-ghost inline-flex items-center gap-2"
          >
            ↓ Download 60-second promo (MP4, ~7 MB)
          </a>
          <a
            data-testid="press-download-video-long"
            href={PROMO_VIDEO_LONG_URL}
            download
            className="btn-ghost inline-flex items-center gap-2"
          >
            ↓ Also: 2-minute cut (MP4, ~11 MB)
          </a>
        </div>
      </Section>

      <Section title="One-liner">
        <p className="serif italic">{ONE_LINER}</p>
      </Section>

      <Section title="Long blurb (≈100 words)">
        <p>{LONG_BLURB}</p>
      </Section>

      <Section title="Quick facts">
        <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-8 gap-y-3">
          {QUICK_FACTS.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground pt-1">
                {k}
              </dt>
              <dd className="serif text-foreground/85">{v}</dd>
            </React.Fragment>
          ))}
        </dl>
      </Section>

      <Section title="What's in the kit">
        <ul className="space-y-2 list-disc pl-6 marker:text-[hsl(var(--accent))]">
          <li>App icon — 512×512 PNG</li>
          <li>Feature graphic — 1024×500 PNG</li>
          <li>5× phone screenshots — 1170×2080 portrait (9:16)</li>
          <li>5× tablet screenshots — 1200×1920 portrait</li>
          <li>5× desktop/Chromebook screenshots — 1920×1080</li>
          <li><code className="mono text-[13px]">PRESS_KIT.md</code> with one-liner, long blurb, quick facts, and this contact info</li>
        </ul>
      </Section>

      <Section title="Suggested pull-quote">
        <blockquote className="serif italic text-xl text-foreground/90 border-l-2 border-[hsl(var(--accent))] pl-6">
          &ldquo;The same truth, spoken in different tongues.&rdquo;
        </blockquote>
      </Section>

      <Section title="Talk to a human">
        <p>
          Coverage requests, interviews, or bespoke asset requests:&nbsp;
          <a href="mailto:press@correlate.online" className="link-underline text-[hsl(var(--primary))]">
            press@correlate.online
          </a>
        </p>
      </Section>

      <p className="mt-16 mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        <Link to="/privacy" className="link-underline">Privacy</Link>
        <span className="mx-3">·</span>
        <Link to="/terms" className="link-underline">Terms</Link>
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="serif text-2xl text-foreground mb-3">{title}</h2>
      <div className="serif text-foreground/85 leading-relaxed text-[16px]">
        {children}
      </div>
    </section>
  );
}
